import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import MonthlyFolderCard from '../components/common/MonthlyFolderCard';
import EmptyState from '../components/common/EmptyState';
import MediaGallery from '../components/common/MediaGallery';
import { FiChevronLeft, FiImage, FiInfo, FiFolder } from 'react-icons/fi';

const ImageProduct = () => {
  const { monthlyUpdates = [] } = useOutletContext() || {};
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);

  // ✅ Group images by month/year
  const months = monthlyUpdates.reduce((acc, update) => {
    if (update.images && update.images.length > 0) {
      const folderSet = new Set(update.images.map(img => img.custom_folder_name || img.folder_name || 'General'));
      acc.push({
        id: update.update_id,
        updateId: update.update_id,
        month: update.month,
        year: update.year,
        images: update.images,
        count: update.images.length,
        folderCount: folderSet.size,
        progress: update.progress_percentage,
        notes: update.notes,
      });
    }
    return acc;
  }, []).sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });

  // ✅ Group by custom folder name (fallback to folder_name, then "General")
  const folders = selectedMonth
    ? Object.values(
      selectedMonth.images.reduce((acc, img) => {
        const key = img.custom_folder_name || img.folder_name || 'General';
        if (!acc[key]) {
          acc[key] = { folderName: key, images: [] };
        }
        acc[key].images.push(img);
        return acc;
      }, {})
    )
    : [];

  const getMonthName = (month) => {
    const names = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return names[month - 1] || `Month ${month}`;
  };

  // ✅ LEVEL 3: Media gallery inside folder
  if (selectedFolder) {
    return (
      <div>
        <button
          onClick={() => setSelectedFolder(null)}
          className="btn-secondary py-sm px-md flex items-center gap-xs text-sm mb-md text-textPrimary hover:bg-bgLight"
        >
          <FiChevronLeft />
          Back to folders
        </button>

        <MediaGallery
          folderName={selectedFolder.folderName}
          month={selectedMonth.month}
          year={selectedMonth.year}
          items={selectedFolder.images}
          onBack={() => setSelectedFolder(null)}
        />
      </div>
    );
  }

  // ✅ LEVEL 2: Folders inside month
  if (selectedMonth) {
    return (
      <div>
        <button
          onClick={() => setSelectedMonth(null)}
          className="btn-secondary py-sm px-md flex items-center gap-xs text-sm mb-md text-textPrimary hover:bg-bgLight"
        >
          <FiChevronLeft />
          Back to all months
        </button>

        <div className="section-title">
          <div className="section-title__icon"><FiFolder /></div>
          <div>
            <h2>Media - {getMonthName(selectedMonth.month)} {selectedMonth.year}</h2>
            <p>{folders.length} folder{folders.length !== 1 ? 's' : ''} • {selectedMonth.count} files total</p>
          </div>
        </div>

        <div className="space-y-3">
          {folders.map((folder) => {
            const pdfCount = folder.images.filter(i => i.file_type === 'pdf').length;
            const imgCount = folder.images.length - pdfCount;
            const summary = pdfCount > 0 ? `${imgCount} photos, ${pdfCount} PDFs` : `${imgCount} photos`;

            return (
              <MonthlyFolderCard
                key={folder.folderName}
                month={selectedMonth.month}
                year={selectedMonth.year}
                customName={folder.folderName}
                count={folder.images.length}
                summary={summary}
                isVideo={false}
                onClick={() => setSelectedFolder(folder)}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // ✅ LEVEL 1: Months
  return (
    <div>
      <div className="section-title">
        <div className="section-title__icon"><FiImage /></div>
        <div>
          <h2>Project Media & Documents</h2>
          <p>Access photos and PDF documents organized by month.</p>
        </div>
      </div>

      {months.length === 0 ? (
        <EmptyState message="No tracking photos or PDF documents have been uploaded for this site yet." />
      ) : (
        <div className="space-y-3">
          {months.map((month) => (
            <MonthlyFolderCard
              key={month.id}
              month={month.month}
              year={month.year}
              count={month.count}
              summary={`${month.folderCount} folder${month.folderCount !== 1 ? 's' : ''}, ${month.count} file${month.count !== 1 ? 's' : ''}`}
              isVideo={false}
              onClick={() => setSelectedMonth(month)}
            />
          ))}
        </div>
      )}

      {months.length > 0 && (
        <div className="info-bar">
          <FiInfo /> Open any month to view folders, photos, and PDF documents.
        </div>
      )}
    </div>
  );
};

export default ImageProduct;